import React, { useState } from 'react';
import { usersAPI } from '../api/client';
import UserAvatar from './UserAvatar';
import { X, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const USER_STATUS_CONFIG = {
  activo:     { label: 'Activo',     bg: 'bg-green-100 text-green-800 border-green-200',   dot: 'bg-green-600' },
  inactivo:   { label: 'Inactivo',   bg: 'bg-slate-100 text-slate-600 border-slate-300',   dot: 'bg-slate-400' },
  suspendido: { label: 'Suspendido', bg: 'bg-red-100 text-red-800 border-red-200',         dot: 'bg-red-600'   },
  temporal:   { label: 'Temporal',   bg: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500' },
};

export function getUserStatus(user) {
  if (user.status) return user.status;
  return user.is_active ? 'activo' : 'inactivo';
}

export function UserStatusBadge({ user }) {
  const status = getUserStatus(user);
  const cfg = USER_STATUS_CONFIG[status] || USER_STATUS_CONFIG.inactivo;
  const daysLeft = user.temporary_access_expires_at
    ? Math.ceil((new Date(user.temporary_access_expires_at) - new Date()) / 86400000)
    : null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
      {status === 'temporal' && daysLeft !== null && (
        <span className="ml-0.5 flex items-center gap-0.5">
          <Clock size={9} /> {daysLeft}d
        </span>
      )}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: 'activo',     emoji: '✅', label: 'Activo',     desc: 'El usuario puede acceder normalmente' },
  { value: 'suspendido', emoji: '⏸',  label: 'Suspendido', desc: 'Bloquea el acceso temporalmente' },
  { value: 'inactivo',   emoji: '❌', label: 'Inactivo',   desc: 'Desactiva la cuenta definitivamente' },
  { value: 'temporal',   emoji: '⏱',  label: 'Temporal',   desc: 'Acceso por tiempo limitado' },
];

export default function ChangeStatusModal({ user, onChanged, onClose }) {
  const [newStatus, setNewStatus] = useState(getUserStatus(user));
  const [expiresAt, setExpiresAt] = useState(
    user.temporary_access_expires_at
      ? user.temporary_access_expires_at.slice(0, 16)
      : ''
  );
  const [reason, setReason] = useState('');
  const [notifyUser, setNotifyUser] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const payload = { status: newStatus, notify_user: notifyUser };
      if (reason) payload.reason = reason;
      if (newStatus === 'temporal' && expiresAt) payload.temporary_access_expires_at = expiresAt;
      await usersAPI.changeStatus(user.id, payload);
      toast.success(`Estado de ${user.name} cambiado a ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      onChanged();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al cambiar estado');
    } finally { setSaving(false); }
  };

  const currentStatus = getUserStatus(user);
  const currentCfg = USER_STATUS_CONFIG[currentStatus] || USER_STATUS_CONFIG.inactivo;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="sm" />
            <h3 className="font-bold text-white text-sm">Cambiar estado de {user.name}</h3>
          </div>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <span className="soem-label">Estado actual:</span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${currentCfg.bg}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${currentCfg.dot}`} />
              {currentCfg.label}
            </span>
          </div>

          <div className="space-y-2">
            {STATUS_OPTIONS.map(opt => (
              <label key={opt.value}
                className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${newStatus === opt.value ? 'border-soem-primary bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="status" value={opt.value} checked={newStatus === opt.value}
                  onChange={() => setNewStatus(opt.value)} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-soem-navy">{opt.emoji} {opt.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {newStatus === 'temporal' && (
            <div>
              <label htmlFor="f-el-acceso-vence-el" className="soem-label block mb-1.5 flex items-center gap-1"><Clock size={11} /> El acceso vence el:</label>
              <input id="f-el-acceso-vence-el" type="datetime-local" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
            </div>
          )}

          <div>
            <label htmlFor="f-motivo-del-cambio-opcional" className="soem-label block mb-1.5">Motivo del cambio (opcional)</label>
            <textarea id="f-motivo-del-cambio-opcional" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-soem-primary"
              rows={2} placeholder="Describe el motivo..." value={reason} onChange={e => setReason(e.target.value)} />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={notifyUser} onChange={e => setNotifyUser(e.target.checked)} />
            <span className="text-sm text-slate-600">Notificar al usuario por email</span>
          </label>

          <div className="flex gap-3 pt-1">
            <button onClick={handleConfirm} disabled={saving || (newStatus === 'temporal' && !expiresAt)}
              className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="confirm-status-btn">
              {saving ? <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Guardando...</> : 'Confirmar cambio'}
            </button>
            <button onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
